import React, { useMemo, useState, useCallback } from 'react';
import { BsChevronRight, BsArrowLeft } from 'react-icons/bs';

const STATUS_COLOURS = {
    'Completed': '#4CAF50',
    'Completed Late': '#FFB300',
    'Not Completed': '#f44336',
    'Not Yet Due': '#90CAF9',
};

const STATUS_ORDER = ['Completed', 'Completed Late', 'Not Completed', 'Not Yet Due'];

const groupByField = (rows, field) => {
    const map = {};
    rows.forEach(row => {
        const key = row[field] || 'Unknown';
        if (!map[key]) {
            map[key] = { label: key, total: 0 };
            STATUS_ORDER.forEach(s => { map[key][s] = 0; });
        }
        map[key].total++;
        const status = row.taskStatus || 'Not Completed';
        if (map[key][status] !== undefined) {
            map[key][status]++;
        }
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
};

const CompletionBars = ({ data, siteFilter, onBarClick, onDrillChange, activeFilter }) => {
    const [tooltip, setTooltip] = useState(null);
    const [drillPath, setDrillPath] = useState([]);

    const drillSite = drillPath.length >= 1 ? drillPath[0] : null;
    const drillTask = drillPath.length >= 2 ? drillPath[1] : null;
    const drillLevel = drillPath.length;

    const barData = useMemo(() => {
        if (!data || data.length === 0) return [];

        let filtered = siteFilter
            ? data.filter(d => d.siteName === siteFilter)
            : data;

        if (drillLevel === 0) {
            return groupByField(filtered, 'siteName');
        }

        filtered = filtered.filter(d => d.siteName === drillSite);

        if (drillLevel === 1) {
            return groupByField(filtered, 'taskDescription');
        }

        filtered = filtered.filter(d => d.taskDescription === drillTask);
        return groupByField(filtered, 'staffName');
    }, [data, siteFilter, drillLevel, drillSite, drillTask]);

    const canDrill = drillLevel < 2;

    const handleDrill = useCallback((label) => {
        const newPath = [...drillPath, label];
        setDrillPath(newPath);
        onDrillChange?.(newPath);
    }, [drillPath, onDrillChange]);

    const handleBack = useCallback(() => {
        const newPath = drillPath.slice(0, -1);
        setDrillPath(newPath);
        onDrillChange?.(newPath);
    }, [drillPath, onDrillChange]);

    const handleReset = useCallback(() => {
        setDrillPath([]);
        onDrillChange?.([]);
    }, [onDrillChange]);

    const onSegmentClick = useCallback((label, status) => {
        // Cross-filter always at site level
        const site = drillLevel === 0 ? label : drillSite;
        onBarClick?.(site, status);
    }, [drillLevel, drillSite, onBarClick]);

    const showTooltip = useCallback((e, label, status, count) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip({
            label,
            status,
            count,
            x: rect.left + rect.width / 2,
            y: rect.top - 8,
        });
    }, []);

    const hideTooltip = useCallback(() => {
        setTooltip(null);
    }, []);

    if (barData.length === 0) {
        return <div className="tagging-bars__empty">No data available</div>;
    }

    const isActive = (label, status) => {
        if (!activeFilter) return false;
        const site = drillLevel === 0 ? label : drillSite;
        if (activeFilter.status) return activeFilter.site === site && activeFilter.status === status;
        if (activeFilter.site === site) return true;
        return false;
    };

    const hasFilter = !!activeFilter;

    let levelLabel = 'All Sites';
    if (drillLevel === 1) levelLabel = `Tasks for: ${drillSite}`;
    else if (drillLevel === 2) levelLabel = `Staff for: ${drillTask}`;

    return (
        <div className="tagging-bars">
            {drillLevel > 0 && (
                <div className="tagging-bars__drill-nav">
                    <button className="tagging-bars__back-btn" onClick={handleBack}>
                        <BsArrowLeft size={12} />
                        <span>Back</span>
                    </button>
                    <span className="tagging-bars__drill-title">{levelLabel}</span>
                    {drillLevel > 1 && (
                        <button className="tagging-bars__reset-btn" onClick={handleReset}>
                            Reset
                        </button>
                    )}
                </div>
            )}
            <div className="tagging-bars__legend">
                <span className="tagging-bars__legend-title">Task Status</span>
                {STATUS_ORDER.map(status => (
                    <div key={status} className="tagging-bars__legend-item">
                        <span
                            className="tagging-bars__legend-swatch"
                            style={{ background: STATUS_COLOURS[status] }}
                        />
                        {status}
                    </div>
                ))}
            </div>
            <div className="tagging-bars__axis">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
            </div>
            <div className="tagging-bars__list">
                {barData.map(row => {
                    const total = row.total;
                    const site = drillLevel === 0 ? row.label : drillSite;
                    const siteInFilter = activeFilter?.site === site;
                    return (
                        <div key={row.label} className="tagging-bars__row">
                            <div className="tagging-bars__site-label" title={row.label}>
                                {row.label}
                            </div>
                            <div className="tagging-bars__bar-track">
                                {STATUS_ORDER.map(status => {
                                    const count = row[status];
                                    if (count === 0) return null;
                                    const pct = (count / total) * 100;
                                    const active = isActive(row.label, status);
                                    const dimmed = hasFilter && !active && !(siteInFilter && !activeFilter.status);
                                    return (
                                        <div
                                            key={status}
                                            className={`tagging-bars__bar-segment${active ? ' tagging-bars__bar-segment--active' : ''}`}
                                            style={{
                                                width: `${pct}%`,
                                                backgroundColor: STATUS_COLOURS[status],
                                                opacity: dimmed ? 0.4 : 1,
                                            }}
                                            onClick={() => onSegmentClick(row.label, status)}
                                            onMouseEnter={(e) => showTooltip(e, row.label, status, count)}
                                            onMouseLeave={hideTooltip}
                                        >
                                            {pct >= 12 && (
                                                <span className="tagging-bars__bar-label">
                                                    {Math.round(pct)}%
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {canDrill && (
                                <button
                                    className="tagging-bars__drill-btn"
                                    onClick={() => handleDrill(row.label)}
                                    title={`Drill into ${row.label}`}
                                >
                                    <BsChevronRight size={10} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {tooltip && (
                <div
                    className="tagging-bars__tooltip"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                    }}
                >
                    <div className="tagging-bars__tooltip-site">{tooltip.label}</div>
                    <div className="tagging-bars__tooltip-row">
                        <span
                            className="tagging-bars__tooltip-swatch"
                            style={{ background: STATUS_COLOURS[tooltip.status] }}
                        />
                        <span>{tooltip.status}</span>
                        <strong>{tooltip.count}</strong>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompletionBars;
